import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { Bell, Check, Pill, GraduationCap, CalendarDays, ClipboardList, ShieldAlert, Gauge } from "lucide-react";
import api from "@/lib/api";

const CATEGORY_META = {
  training:   { label: "Training",   icon: GraduationCap, tone: "text-sky-700 bg-sky-50 border-sky-200 dark:text-sky-300 dark:bg-sky-950/40 dark:border-sky-800" },
  medication: { label: "Medication", icon: Pill,          tone: "text-amber-700 bg-amber-50 border-amber-200 dark:text-amber-300 dark:bg-amber-950/40 dark:border-amber-800" },
  leave:      { label: "Leave",      icon: CalendarDays,  tone: "text-emerald-700 bg-emerald-50 border-emerald-200 dark:text-emerald-300 dark:bg-emerald-950/40 dark:border-emerald-800" },
  care:       { label: "Care",       icon: ClipboardList, tone: "text-indigo-700 bg-indigo-50 border-indigo-200 dark:text-indigo-300 dark:bg-indigo-950/40 dark:border-indigo-800" },
  incident:   { label: "Governance", icon: ShieldAlert,   tone: "text-slate-800 bg-slate-100 border-slate-300 dark:text-slate-200 dark:bg-slate-800 dark:border-slate-700" },
  governance: { label: "Governance", icon: Gauge,         tone: "text-slate-800 bg-slate-100 border-slate-300 dark:text-slate-200 dark:bg-slate-800 dark:border-slate-700" },
};

const PRIORITY_DOT = {
  critical: "bg-rose-500",
  high:     "bg-amber-500",
  normal:   "bg-sky-500",
  low:      "bg-slate-400",
};

const FILTERS = [
  { key: "all",        label: "All" },
  { key: "unread",     label: "Unread" },
  { key: "medication", label: "Medication" },
  { key: "training",   label: "Training" },
  { key: "leave",      label: "Leave" },
  { key: "care",       label: "Care" },
  { key: "governance", label: "Governance" },
];

function timeAgo(ts) {
  if (!ts) return "";
  const then = new Date(ts).getTime();
  if (!then) return "";
  const diff = (Date.now() - then) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ items: [], unread: 0 });
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/notifications", { params: { limit: 200 } });
      setData(r.data || { items: [], unread: 0 });
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, []);

  const refreshBadgeOnly = useCallback(async () => {
    try {
      const r = await api.get("/notifications/unread-count");
      setData((d) => ({ ...d, unread: r.data?.unread ?? d.unread }));
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    refreshBadgeOnly();
    const t = setInterval(refreshBadgeOnly, 60_000);
    return () => clearInterval(t);
  }, [refreshBadgeOnly]);

  useEffect(() => { if (open) refresh(); }, [open, refresh]);

  const filtered = useMemo(() => {
    let items = data.items || [];
    if (filter === "unread") items = items.filter((n) => !n.read);
    else if (filter !== "all") items = items.filter((n) => n.category === filter);
    return items;
  }, [data.items, filter]);

  const markOne = async (id, link) => {
    try {
      await api.post("/notifications/mark-read", { ids: [id] });
      setData((d) => ({
        ...d,
        items: d.items.map((n) => n.id === id ? { ...n, read: true } : n),
        unread: Math.max(0, d.unread - 1),
      }));
    } catch { /* ignore */ }
    if (link) window.location.href = link;
  };

  const markAll = async () => {
    try {
      const r = await api.post("/notifications/mark-all-read");
      if (r.data?.marked > 0) {
        setData((d) => ({
          ...d,
          items: d.items.map((n) => ({ ...n, read: true })),
          unread: 0,
        }));
      }
    } catch { /* ignore */ }
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        onBlur={() => setTimeout(() => setOpen(false), 220)}
        data-testid="notification-bell"
        title={`${data.unread} unread notification${data.unread === 1 ? "" : "s"}`}
        className="relative h-7 w-7 flex items-center justify-center rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-300 hover:text-emerald-700 hover:border-emerald-300 transition-colors"
        aria-label="Notification centre"
      >
        <Bell className="w-4 h-4" />
        {data.unread > 0 && (
          <span
            className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-emerald-600 text-white text-[9px] font-bold flex items-center justify-center tabular-nums"
            data-testid="notification-bell-badge"
          >
            {data.unread > 99 ? "99+" : data.unread}
          </span>
        )}
      </button>

      {open && (
        <div
          onMouseDown={(e) => e.preventDefault()}
          className="absolute right-0 mt-1 z-50 w-[380px] max-h-[560px] bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-md shadow-xl overflow-hidden flex flex-col"
          data-testid="notification-panel"
        >
          <div className="px-3 py-2 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between">
            <div className="text-xs font-semibold text-slate-800 dark:text-slate-100">
              Notifications
              {data.unread > 0 && (
                <span className="ml-1.5 text-[10px] text-emerald-700 dark:text-emerald-300 font-bold tabular-nums" data-testid="notification-panel-unread">
                  ({data.unread} unread)
                </span>
              )}
            </div>
            <button
              onClick={markAll}
              disabled={!data.unread}
              className="text-[10px] uppercase tracking-widest text-emerald-700 dark:text-emerald-400 hover:underline disabled:opacity-40"
              data-testid="notification-mark-all"
            >
              Mark all read
            </button>
          </div>

          <div className="px-2 py-1.5 border-b border-slate-100 dark:border-slate-800 flex gap-1 overflow-x-auto">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                data-testid={`notification-filter-${f.key}`}
                className={`px-2 py-0.5 rounded-full text-[10px] font-semibold whitespace-nowrap border transition-colors ${
                  filter === f.key
                    ? "bg-slate-900 text-white border-slate-900 dark:bg-slate-100 dark:text-slate-900 dark:border-slate-100"
                    : "bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-700 hover:border-slate-400"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>

          <ul className="flex-1 overflow-y-auto divide-y divide-slate-100 dark:divide-slate-800">
            {loading && (
              <li className="px-3 py-6 text-center text-xs text-slate-500">Loading…</li>
            )}
            {!loading && filtered.length === 0 && (
              <li className="px-3 py-8 text-center text-xs text-slate-500" data-testid="notification-empty">
                {filter === "unread" ? "Nothing unread — you're all caught up." : "No notifications yet."}
              </li>
            )}
            {filtered.map((n) => {
              const meta = CATEGORY_META[n.category] || CATEGORY_META.governance;
              const Icon = meta.icon;
              return (
                <li key={n.id}
                    onClick={() => markOne(n.id, n.link)}
                    data-testid={`notification-item-${n.id}`}
                    className={`px-3 py-2 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50 ${n.read ? "opacity-60" : ""}`}>
                  <div className="flex items-start gap-2">
                    <span className={`shrink-0 mt-0.5 w-1.5 h-1.5 rounded-full ${PRIORITY_DOT[n.priority] || PRIORITY_DOT.normal}`} />
                    <span className={`shrink-0 inline-flex items-center gap-1 px-1.5 h-4 rounded-sm border text-[9px] font-semibold uppercase tracking-widest ${meta.tone}`}>
                      <Icon className="w-2.5 h-2.5" /> {meta.label}
                    </span>
                    <span className="ml-auto text-[10px] text-slate-400">{timeAgo(n.created_at)}</span>
                  </div>
                  <div className="mt-1 text-xs font-semibold text-slate-800 dark:text-slate-100">
                    {n.title}
                  </div>
                  <div className="text-[11px] text-slate-600 dark:text-slate-300 line-clamp-2">
                    {n.message}
                  </div>
                  {n.person && (
                    <div className="text-[10px] text-slate-500 mt-0.5">· {n.person}</div>
                  )}
                </li>
              );
            })}
          </ul>

          <div className="border-t border-slate-200 dark:border-slate-800 px-3 py-1.5 text-right">
            <Link to="/dashboard" className="text-[10px] text-slate-500 hover:text-slate-800 dark:hover:text-slate-100">
              Go to dashboard →
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
